import * as React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { DayPicker, DayPickerProps } from "react-day-picker";
import { enUS, fr } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";

// Props génériques pour accepter n'importe quel mode
export type CalendarProps = DayPickerProps & {
  onClose?: () => void; // facultatif pour fermer le calendrier après sélection
  onSelect?: (day: Date | undefined, selected: any) => void; // ajout de onSelect
};

export function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  selected,
  mode = "single",
  onClose,
  ...props
}: CalendarProps) {
  const { locale } = useLanguage();
  const dayPickerLocale = locale === "fr" ? fr : enUS;

  // Mois contrôlé : si mode single et selected est une Date, on l'utilise
  const initialMonth =
    mode === "single" && selected instanceof Date ? selected : new Date();
  const [month, setMonth] = React.useState<Date>(initialMonth);

  const [firstDay, setFirstDay] = React.useState(() => {
      const savedFirstDay = localStorage.getItem("calendar-first-day");
      return savedFirstDay ? parseInt(savedFirstDay, 10) : 1;
  });

  React.useEffect(() => {
    if (mode === "single" && selected instanceof Date) {
      setMonth(selected);
    }
  }, [selected, mode]);

  return (
    <DayPicker
      mode={mode}
      selected={selected as any} // TS ne sait pas quel type selon mode
      month={month}
      onMonthChange={setMonth}
      onSelect={(day, ...rest) => {
        (props.onSelect as any)?.(day, ...rest); // cast pour éviter erreur TS
        if (day && onClose) onClose(); // fermeture automatique si défini
      }}
      showOutsideDays={showOutsideDays}
      locale={dayPickerLocale}
      weekStartsOn={Number(firstDay) as 0 | 1 | 2 | 3 | 4 | 5 | 6}
      className={cn("p-3 pointer-events-auto", className)}
      classNames={{
        months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
        month: "space-y-4",
        caption: "flex justify-center pt-1 relative items-center",
        caption_label: "text-sm font-medium",
        nav: "space-x-1 flex items-center",
        nav_button: cn(
          buttonVariants({ variant: "outline" }),
          "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100"
        ),
        nav_button_previous: "absolute left-1",
        nav_button_next: "absolute right-1",
        table: "w-full border-collapse space-y-1",
        head_row: "flex",
        head_cell: "text-muted-foreground rounded-md w-9 font-normal text-[0.8rem]",
        row: "flex w-full mt-2",
        cell: "h-9 w-9 text-center text-sm p-0 relative [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected])]:bg-accent first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20",
        day: cn(
          buttonVariants({ variant: "ghost" }),
          "h-9 w-9 p-0 font-normal aria-selected:opacity-100"
        ),
        day_range_end: "day-range-end",
        day_selected:
          "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground",
        day_today: "bg-accent text-accent-foreground",
        day_outside:
          "day-outside text-muted-foreground opacity-50 aria-selected:bg-accent/50 aria-selected:text-muted-foreground aria-selected:opacity-30",
        day_disabled: "text-muted-foreground opacity-50",
        day_range_middle: "aria-selected:bg-accent aria-selected:text-accent-foreground",
        day_hidden: "invisible",
        ...classNames,
      }}
      components={{
        IconLeft: (props) => <ChevronLeft className="h-4 w-4" {...props} />,
        IconRight: (props) => <ChevronRight className="h-4 w-4" {...props} />,
      }}
      {...props}
    />
  );
}

Calendar.displayName = "Calendar";
